import { useId, useState } from "react";
import { Aviso, Boton, Modal } from "../../components/ui/primitivos";
import { largoDelMotivo, MOTIVO_MAX, MOTIVO_MIN } from "./cambio-de-estado";
import { motivoOpcionalValido } from "./eliminar";

/**
 * Confirmar la eliminación de una OT en `BORRADOR` (ADR 0100 B3, B4).
 *
 * El motivo es opcional; si viene, la misma regla de 10 a 500 que el resto.
 * Se monta abierto y se desmonta al cerrar, como `DialogoCambioDeEstado`. El
 * error que se muestra es el del worker, tal cual (403, 409, 422).
 */
export function DialogoEliminarOt({
  numero,
  onCerrar,
  alConfirmar,
}: {
  numero: string;
  onCerrar: () => void;
  alConfirmar: (motivo: string) => Promise<void>;
}) {
  const idMotivo = useId();
  const idContador = useId();
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  const largo = largoDelMotivo(motivo);
  const listo = motivoOpcionalValido(motivo) && !trabajando;

  async function confirmar() {
    setTrabajando(true);
    setError(null);
    try {
      await alConfirmar(largo === 0 ? "" : motivo.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo eliminar la OT");
      setTrabajando(false);
    }
  }

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={`Eliminar ${numero}`}
      descripcion="La OT pasa a Eliminada y no se puede restaurar. Queda en el historial con tu nombre."
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label htmlFor={idMotivo} className="block text-neutral-600 text-sm">
            Motivo (opcional)
          </label>
          <textarea
            id={idMotivo}
            rows={3}
            value={motivo}
            aria-describedby={idContador}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Por qué se elimina…"
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm"
          />
          <p
            id={idContador}
            className={
              motivoOpcionalValido(motivo) ? "text-neutral-500 text-xs" : "text-red-700 text-xs"
            }
          >
            {largo}/{MOTIVO_MAX} · mínimo {MOTIVO_MIN} caracteres
          </p>
        </div>

        {error ? <Aviso>{error}</Aviso> : null}

        <div className="flex justify-end gap-2">
          <Boton variante="secundario" onClick={onCerrar}>
            Volver
          </Boton>
          <Boton variante="peligro" disabled={!listo} onClick={() => void confirmar()}>
            Eliminar OT
          </Boton>
        </div>
      </div>
    </Modal>
  );
}
